import axios from "@/configuration/axios";
import env from "@/configuration/env";

export default {
  strict: true,
  namespaced: true,
  state: {
    isInitialized: false,
    user: null,
  },
  getters: {
    isInitialized: state => state.isInitialized,
    isAuthenticated: state => !!state.user,
    userId: state => (state.user ? state.user.userId : null),
  },
  mutations: {
    initialize: state => (state.isInitialized = true),
    setUser: (state, user) => {
      state.user = user;
    },
    clearUser: state => {
      state.user = null;
    },
  },
  actions: {
    initialize: async ({ commit }) => {
      try {
        const response = await axios.get("/v1/auth/me");

        // Only accept if this is a WorkOS user (wos_session cookie is HttpOnly, can't check client-side)
        if (response.data.workosUserId) {
          commit("setUser", response.data);
        }
      } catch (error) {
        // Only clear user on explicit 401, not on transport errors like abort/partial transfer
        if (error.response?.status === 401) {
          commit("clearUser");
        } else {
          console.warn("[WorkOS] Non-401 error during init, NOT clearing user", error);
        }
      }

      commit("initialize");
      return { success: true };
    },
    // eslint-disable-next-line no-empty-pattern
    signIn: async ({}, emailAddress) => {
      // Redirect to WorkOS hosted auth UI
      // The API callback endpoint will handle the OAuth flow and set cookies
      const clientId = env.workos_client_id;
      if (!clientId) {
        throw new Error("Missing env.workos_client_id");
      }

      const apiBaseUrl = env.workos_api_baseurl || "https://api.workos.com";

      // Use API base address for redirect URI (works in dev localhost:5050 and prod)
      const redirectUri = `${env.api_base_address}/v1/auth/callback`;
      let authUrl = `${apiBaseUrl}/user_management/authorize?response_type=code&client_id=${clientId}&provider=authkit&redirect_uri=${encodeURIComponent(
        redirectUri
      )}`;

      // Add login_hint to pre-fill email on WorkOS login screen
      if (emailAddress) {
        authUrl += `&login_hint=${encodeURIComponent(emailAddress)}`;
      }

      window.location = authUrl;
    },

    signOut: async ({ commit }, { redirect = true } = {}) => {
      try {
        // Call API to clear cookies and get WorkOS logout URL
        const response = await axios.post("/v1/auth/logout");
        commit("clearUser");

        const logoutUrl = response.data.logoutUrl;
        const signinUrl = "/signin";
        const redirectUrl = logoutUrl || signinUrl;

        if (redirect) {
          if (logoutUrl) {
            // Redirect to WorkOS logout to invalidate upstream session
            // WorkOS will redirect back to the configured "Logout redirect URI" in your WorkOS dashboard
            window.location = logoutUrl;
          } else {
            // No WorkOS session, just redirect to sign in
            window.location = signinUrl;
          }
        }

        return { success: true, redirect_url: redirectUrl };
      } catch (error) {
        // Even if logout fails, clear local state and redirect
        commit("clearUser");
        if (redirect) {
          window.location = "/signin";
        }
        return { success: true, redirect_url: "/signin" };
      }
    },
  },
};
